/* global ClipboardItem */
import $ from 'jquery'

import { onPageLoad } from '~/common/extensions/dom_ready'
import { prepareClipboardData } from '~/common/helpers/clipboard_data'
import { haptics } from '~/common/helpers/haptics'

let modifier, showTooltip

showTooltip = function (elm, msg, { failed = false } = {}) {
  let $elm, $icon, org_icon, org_msg
  $elm = $(elm)
  // `$elm.tooltip` (the Bootstrap jQuery plugin) is truthy globally whenever
  // Bootstrap is loaded, so it can't tell us whether THIS element uses a
  // modern (tippy) tooltip. Modern-tooltip elements update their existing
  // tippy via the `updateContent` event — calling `.tooltip('show')` on them
  // would spawn a second, Bootstrap tooltip alongside the tippy one.
  const isModern =
    !!$elm.data('modern-tooltip') ||
    ($elm.attr('data-controller') || '').split(/\s+/).includes('popover')
  org_msg =
    $elm.attr('data-copy-title') ||
    $elm.attr('data-original-title') ||
    $elm.attr('data-title')
  const org_label = $elm.attr('aria-label')
  $elm
    .attr('data-original-title', msg)
    .attr('aria-label', msg)
    .attr('data-placement', 'bottom')
  if ($elm.tooltip && !isModern) {
    $elm.tooltip('show')
  } else {
    $elm[0].dispatchEvent(
      new CustomEvent('updateContent', { detail: { content: msg, show: true } })
    )
  }
  $elm.attr('data-original-title', org_msg)
  // A failed copy keeps its own icon: the copied check would say it worked.
  const swapIcon = !failed && $elm.data('copiedIcon')
  if (swapIcon) {
    $icon = $elm.find('i')
    org_icon = $icon[0]
    $icon.replaceWith($elm.data('copiedIcon'))
  }
  // Put the tooltip and label back either way, or the message (an error above all) stays on
  // the button for good.
  return setTimeout(function () {
    if (org_label === undefined) $elm.removeAttr('aria-label')
    else $elm.attr('aria-label', org_label)
    if (org_msg && (!$elm.tooltip || isModern)) {
      $elm[0].dispatchEvent(
        new CustomEvent('updateContent', {
          detail: { content: org_msg, show: false },
        })
      )
    }
    if (swapIcon) $elm.find('i').replaceWith(org_icon)
  }, 2000)
}

modifier = /Mac/i.test(navigator.userAgent) ? '⌘' : 'Ctrl'

// The page's own (translated) words for a failed copy, from the button's data (ClippyHelper),
// or English where a button doesn't carry them.
const say = (element, key, fallback) => $(element).data(key) || fallback

// Waits briefly for a promise's rejection reason: an answer the copy can't wait on forever.
const reasonWithin = (promise, ms) =>
  Promise.race([
    promise.then(
      () => null,
      (error) => error
    ),
    new Promise((resolve) => setTimeout(() => resolve(null), ms)),
  ])

// `{ text, html? }` from a copy endpoint (sources, chat messages). Bounded: the clipboard write
// waits on it, and a request that never answers would leave the click with no word at all.
const FETCH_TIMEOUT_MS = 10_000
const fetchCopy = async (url) => {
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
  })
  if (!response.ok) {
    // The endpoint's own reason (nothing to copy yet), and the status for when it gives none.
    const body = await response.json().catch(() => ({}))
    const error = new Error(`Copy failed: HTTP ${response.status}`)
    error.reason = body.error
    error.status = response.status
    throw error
  }
  return response.json()
}

onPageLoad('clipboard', function () {
  return $(document).on(
    'click',
    '*[data-clipboard-text], *[data-clipboard-selectors], *[data-clipboard-url]',
    async function (e) {
      const element = this
      if (!element.classList.contains('clippy-dont-prevent-default')) {
        e.preventDefault()
        e.stopImmediatePropagation()
      }

      const url = element.dataset.clipboardUrl
      // A slow answer to an earlier click must not overwrite this one's.
      const attempt = (element.clippyAttempt = (element.clippyAttempt || 0) + 1)
      let copy
      try {
        // Large text is fetched on click instead of sitting in the page. The write
        // starts inside the click with a promised blob, which Safari needs to allow it.
        if (url) {
          copy = fetchCopy(url)
          const blob = (key, type) =>
            copy.then((data) => new Blob([data[key] || ''], { type }))
          const items = { 'text/plain': blob('text', 'text/plain') }
          if (element.dataset.clipboardRich)
            items['text/html'] = blob('html', 'text/html')
          await navigator.clipboard.write([new ClipboardItem(items)])
          showTooltip(element, $(element).data('copied'))
          haptics.tap()
          return
        }

        const clipboardData = prepareClipboardData(element)
        await (Object.values(clipboardData).length > 1
          ? navigator.clipboard.write([new ClipboardItem(clipboardData)])
          : navigator.clipboard.writeText(clipboardData['text/plain']))

        window.getSelection().removeAllRanges()
        showTooltip(element, $(element).data('copied'))
        haptics.tap()
      } catch (e) {
        // Fetched text was never on the page, so there's nothing to select and copy by hand.
        let message
        if (url) {
          // The clipboard write may reject with its own error (or first, with the fetch still
          // pending): the fetch has the reason, if it answers soon.
          const reason = (copy && (await reasonWithin(copy, 1500))) || e
          if (element.clippyAttempt !== attempt) return
          message =
            reason.reason ||
            (reason.status === 401 || reason.status === 403
              ? say(
                  element,
                  'copy-no-access',
                  "You're signed out or no longer have access. Reload the page and try again."
                )
              : say(element, 'copy-failed', "Couldn't copy. Try again."))
        } else {
          const shortcut = `${modifier}-${element.action === 'cut' ? 'X' : 'C'}`
          message = say(
            element,
            element.action === 'cut' ? 'cut-shortcut' : 'copy-shortcut',
            `Press %{shortcut} to ${element.action === 'cut' ? 'cut' : 'copy'}`
          ).replace('%{shortcut}', shortcut)
        }
        showTooltip(element, message, { failed: true })
        haptics.reject()
      }
    }
  )
})
